import { useTheme } from '@/hooks/useTheme';
import { cn } from '@/lib/cn';

interface ThemeToggleProps {
  /**
   * 'icon' = circular 40×40 control, good for TopBar / public pages.
   * 'inline' = row with label, good for Settings-style surfaces.
   */
  variant?: 'icon' | 'inline';
  className?: string;
  labelLight?: string;
  labelDark?: string;
  ariaLabel?: string;
}

/**
 * Dark/light toggle. Deliberately minimal — a single pressable control,
 * no tri-state system picker here (reset-to-system lives in Settings).
 *
 * Motion:
 *   - Icon crossfade at 160ms ease-out.
 *   - Scale(0.94) on active for tactile press feedback (Emil rule).
 *   - No animation on the actual theme class swap; browsers repaint fast
 *     enough and animating background colors is expensive.
 */
export function ThemeToggle({
  variant = 'icon',
  className,
  labelLight = 'Light',
  labelDark = 'Dark',
  ariaLabel,
}: ThemeToggleProps) {
  const { resolved, toggle } = useTheme();
  const isDark = resolved === 'dark';

  if (variant === 'inline') {
    return (
      <button
        type="button"
        onClick={toggle}
        aria-label={ariaLabel ?? `Switch to ${isDark ? labelLight : labelDark} theme`}
        aria-pressed={isDark}
        className={cn(
          'pressable inline-flex min-h-[44px] w-full items-center justify-between gap-3 rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-ink hover:bg-surface-3',
          className,
        )}
      >
        <span className="flex items-center gap-2">
          <IconSwap isDark={isDark} />
          <span className="font-medium">{isDark ? labelDark : labelLight}</span>
        </span>
        <span className="text-xs text-ink-muted">{isDark ? '◐' : '◑'}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={ariaLabel ?? `Switch to ${isDark ? labelLight : labelDark} theme`}
      aria-pressed={isDark}
      className={cn(
        'pressable focus-ring inline-flex h-11 w-11 items-center justify-center rounded-full border border-line bg-surface-2 text-ink hover:bg-surface-3',
        className,
      )}
    >
      <IconSwap isDark={isDark} />
    </button>
  );
}

/**
 * Crossfaded sun/moon. We stack both icons and opacity-toggle them so the
 * swap is a single painted frame — no layout shift, no width jitter when
 * the svg path changes.
 */
function IconSwap({ isDark }: { isDark: boolean }) {
  return (
    <span className="relative inline-block h-4 w-4">
      <SunIcon
        className={cn(
          'absolute inset-0 transition-opacity duration-160 ease-out-soft',
          isDark ? 'opacity-0' : 'opacity-100',
        )}
      />
      <MoonIcon
        className={cn(
          'absolute inset-0 transition-opacity duration-160 ease-out-soft',
          isDark ? 'opacity-100' : 'opacity-0',
        )}
      />
    </span>
  );
}

function SunIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      aria-hidden
    >
      <circle cx="8" cy="8" r="3" />
      <path d="M8 1.5v1.5M8 13v1.5M1.5 8h1.5M13 8h1.5M3.2 3.2l1.1 1.1M11.7 11.7l1.1 1.1M3.2 12.8l1.1-1.1M11.7 4.3l1.1-1.1" />
    </svg>
  );
}

function MoonIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M13.5 9.5A5.5 5.5 0 0 1 6.5 2.5a.5.5 0 0 0-.7-.6A6.5 6.5 0 1 0 14.1 10.2a.5.5 0 0 0-.6-.7Z" />
    </svg>
  );
}
